import { Fragment, useRef } from 'react';

import { EmptyState, InteractiveTable, LoadingTableRows, MoreButton, Table } from '@ingrid/luterkort-react';

import { Ellipsis } from '~src/components/Ellipsis/Ellipsis';
import { useTranslation } from '~src/i18n/use-translation';

import { useBookingRulesQuery } from '../queries';
import { DetailsDrawer } from './details-drawer';
import { Menu } from './menu';

interface Props {
  loadRowCount: number;
}

export function ListTable({ loadRowCount }: Props) {
  const { data, isLoading } = useBookingRulesQuery();

  const { highlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();

  const highlightedItem = data.find(i => i.method === highlightedKey);
  const table = useRef<HTMLTableElement>(null);

  if (!data.length && !isLoading) {
    return <EmptyState title={t('bookingRules.list.empty')} />;
  }

  return (
    <Fragment>
      <InteractiveTable.Table ref={table}>
        <thead>
          <Table.Row>
            <Table.HeadCell css={{ width: '20%' }}>{t('common.shippingMethod')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '25%' }}>{t('common.ruleName')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '30%' }}>{t('common.conditions')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '25%' }}>{t('common.actions')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '35px' }} />
          </Table.Row>
        </thead>
        <tbody>
          {data.map((cbm, index) => (
            <InteractiveTable.Row key={`cbm-${index}`} value={cbm.method!}>
              <Table.Cell>
                <Ellipsis>{cbm.method}</Ellipsis>
              </Table.Cell>
              <Table.Cell>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {cbm.steps?.map((step, si) => (
                    <Ellipsis key={`cbm-${cbm.method}-step-${si}-name`}>{step.name}</Ellipsis>
                  ))}
                </div>
              </Table.Cell>
              <Table.Cell>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {cbm.steps?.map(step =>
                    step.filters?.map((filter, fi) => (
                      <Ellipsis key={`cbm-${cbm.method}-step-${fi}-condition`}>{filter.expression}</Ellipsis>
                    ))
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {cbm.steps?.map(step =>
                    step.actions?.map((action, ai) => (
                      <Ellipsis key={`cbm-${cbm.method}-step-${ai}-action`}>{action.expression}</Ellipsis>
                    ))
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <Menu customBookingMethod={cbm} trigger={<MoreButton size="sm" />} />
              </Table.Cell>
            </InteractiveTable.Row>
          ))}
          {isLoading && <LoadingTableRows tableRef={table} rowCount={loadRowCount || 0} />}
        </tbody>
      </InteractiveTable.Table>

      <DetailsDrawer item={highlightedItem} />
    </Fragment>
  );
}
